/**
 * navigation-breadcrumb.js (served as search-breadcrumb.js)
 *
 * Ensures the PDP breadcrumb shows the collection (or search results page) the
 * customer actually navigated FROM, rather than defaulting to the product's first
 * assigned collection (which may not match the customer's browsing context).
 *
 * Three responsibilities:
 *
 * 1. COLLECTION PAGE — Capture the current collection's URL and title into
 *    sessionStorage under 'ea-nav-ref' whenever the user clicks a product link.
 *    The title/URL are read from [data-breadcrumb-collection] in the page breadcrumb
 *    (present on all collection templates), with a heading/URL fallback.
 *
 * 2. SEARCH PAGE — When the page is /search with a non-empty ?q param, capture the
 *    search query and URL into sessionStorage under 'ea-nav-ref'.
 *
 * 3. PDP — Read and immediately consume 'ea-nav-ref'. If it contains navigation
 *    context, rewrite the [data-breadcrumb-collection] anchor's href and text.
 *    Falls back gracefully when no history data is available (Liquid's
 *    bc_product.collections.first remains as-rendered).
 *
 * ES module — no @theme/* imports, no importmap registration required.
 * sessionStorage is tab-scoped, matching the scroll-restoration pattern in this codebase.
 */

const NAV_KEY = 'ea-nav-ref';
// Legacy key from the search-only implementation — read on restore for deploy-day compat.
const LEGACY_KEY = 'ea-search-ref';

/**
 * Capture the current collection context when the user clicks a product link.
 * Reads from [data-breadcrumb-collection] (the breadcrumb block's collection anchor)
 * for a reliable title/URL; falls back to the first h1 + current URL.
 */
function initCollectionCapture() {
  const collectionAnchor = document.querySelector('[data-breadcrumb-collection]');
  let collectionUrl, collectionTitle;

  if (collectionAnchor) {
    collectionUrl = collectionAnchor.href;
    collectionTitle = collectionAnchor.textContent.trim();
  } else {
    // Breadcrumb block not present on this collection page — use page heading + URL.
    collectionUrl = window.location.href;
    const heading = document.querySelector('h1');
    collectionTitle = heading ? heading.textContent.trim() : '';
  }

  if (!collectionTitle || !collectionUrl) return;

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href*="/products/"]');
    if (!anchor) return;

    try {
      sessionStorage.setItem(
        NAV_KEY,
        JSON.stringify({ type: 'collection', url: collectionUrl, title: collectionTitle })
      );
    } catch (_e) {
      // sessionStorage may be unavailable in certain privacy modes — fail silently.
    }
  });
}

/**
 * Capture the search query and URL when the user clicks a product link on /search.
 */
function initSearchCapture() {
  const params = new URLSearchParams(window.location.search);
  const q = params.get('q');

  if (!q || !q.trim()) return;

  const searchUrl = window.location.href;
  const trimmedQ = q.trim();

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href*="/products/"]');
    if (!anchor) return;

    try {
      sessionStorage.setItem(
        NAV_KEY,
        JSON.stringify({ type: 'search', url: searchUrl, title: `Search: "${trimmedQ}"`, q: trimmedQ })
      );
    } catch (_e) {
      // sessionStorage may be unavailable in certain privacy modes — fail silently.
    }
  });
}

/**
 * On PDP, read and consume the stored navigation context, then mutate
 * [data-breadcrumb-collection] to reflect where the customer came from.
 * Falls back silently when no context is stored (Liquid's default remains).
 */
function initBreadcrumbRestore() {
  // Only run on product pages — prevents consuming the stored context when the
  // customer navigates Collection A → Collection B (both pages run this script).
  if (!window.location.pathname.includes('/products/')) return;

  const nav = document.querySelector('[data-search-breadcrumb]');
  if (!nav) return;

  let stored = null;

  try {
    // Read but do NOT remove — keeping the entry lets a page refresh restore the
    // breadcrumb correctly. The entry is naturally overwritten when the customer
    // navigates to another collection or search page, and expires when the tab closes.
    // Only remove the legacy key so it doesn't linger alongside the new one.
    const raw = sessionStorage.getItem(NAV_KEY) || sessionStorage.getItem(LEGACY_KEY);
    sessionStorage.removeItem(LEGACY_KEY);

    if (raw) stored = JSON.parse(raw);
  } catch (_e) {
    // Malformed JSON or sessionStorage unavailable — fail silently.
    return;
  }

  if (!stored) return;

  // Normalise legacy format: { q, url } → { title, url }
  if (!stored.title && stored.q) {
    stored.title = `Search: "${stored.q}"`;
  }

  if (!stored.url || !stored.title) return;

  const collectionAnchor = nav.querySelector('[data-breadcrumb-collection]');
  if (!collectionAnchor) return;

  collectionAnchor.href = stored.url;
  collectionAnchor.textContent = stored.title;

  // Reveal the wrapper (separator + anchor) that Liquid hid by default on PDP pages.
  // display:contents makes the span transparent to the parent flex layout so its
  // children (separator + anchor) behave as direct flex items — no stacking.
  const wrap = nav.querySelector('[data-breadcrumb-collection-wrap]');
  if (wrap) wrap.style.display = 'contents';
}

// --- Entry point ---

const { pathname } = window.location;
const isProductPage = pathname.includes('/products/');
const isCollectionPage = pathname.includes('/collections/') && !isProductPage;
const isSearchPage = pathname.endsWith('/search');
const isContextPage = isProductPage || isCollectionPage || isSearchPage;

if (isCollectionPage) {
  initCollectionCapture();
} else if (isSearchPage) {
  initSearchCapture();
} else if (!isContextPage) {
  // User landed on a page outside the collection→product flow (e.g. homepage, blog,
  // /pages/*). Clear stored context so subsequent product navigations from here use
  // the Liquid fallback (bc_product.collections.first) instead of stale history.
  try { sessionStorage.removeItem(NAV_KEY); } catch (_e) {}
}

initBreadcrumbRestore();
